/**
 * FERP Portal - Sidebar Entity Create Panel
 *
 * Renders an inline entity creation form inside the expanded sidebar.
 * Works for ALL subgraphs and ALL 25 primitives — DRY pattern.
 *
 * Triggered by the "+" quick-create button on nav items.
 * Uses EntityFormGenerator (backend-driven, DRR-055) — zero hardcoded fields.
 *
 * "Scan" button activates the intake intelligence pipeline via useScanToFill:
 * Camera capture → device OCR → classifyIntakeText → pre-fill form fields.
 * DRR-159: device-native OCR, T4 media stays on device.
 *
 * FERP Principles:
 * - Data-driven: Form schema from backend via EntityFormGenerator
 * - DRY: One component for all subgraph + primitive entity creation
 * - DRY: Scan logic in reusable useScanToFill hook (not inlined)
 * - Dumb Frontend: Shows "Form Not Available" if no backend schema exists
 * - Repository pattern: Mutations via usePrimitiveMutations for ferp-core entities
 */

import React, { useMemo, useState, useCallback, useRef } from 'react';
import {
  Box,
  Group,
  Title,
  ActionIcon,
  ScrollArea,
  Alert,
  Text,
  Tooltip,
  Loader,
} from '@mantine/core';
import { IconX, IconAlertTriangle, IconScan, IconCheck } from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { EntityFormGenerator, type UIContext, useScanToFill } from '@ferp/workflow-forms';
import { useTenantId } from '@/auth';
import { usePrimitiveMutations } from '@/hooks/usePrimitiveMutations';
import type { PrimitiveType } from '@/config/primitive-metadata';

export interface SidebarEntityCreatePanelProps {
  subgraph: string;
  entityType: string;
  entityLabel: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function SidebarEntityCreatePanel({
  subgraph,
  entityType,
  entityLabel,
  onClose,
  onSuccess,
}: Readonly<SidebarEntityCreatePanelProps>): React.ReactElement {
  const tenantId = useTenantId();
  const apolloClient = useApolloClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // DRY: Scan-to-fill logic in reusable hook (DRR-159)
  const { initialValues, statusMessage, status, scanning, ocrAvailable, processImage } =
    useScanToFill();

  // For ferp-core entities, PascalCase entityType → lowercase PrimitiveType ('Task' → 'task')
  const primitiveType = (entityType.charAt(0).toLowerCase() + entityType.slice(1)) as PrimitiveType;

  // Always call hook (rules of hooks) — for non-core subgraphs the result is unused
  const { create: createPrimitive, loading: mutationLoading } = usePrimitiveMutations(primitiveType);

  const context: UIContext = useMemo(
    () => ({ mode: 'CREATE', tenantId: tenantId ?? '' }),
    [tenantId],
  );

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setSubmitError(null);
      try {
        if (subgraph === 'core') {
          await createPrimitive({ name: String(values.name ?? ''), ...values });
        }
        await apolloClient.refetchQueries({ include: 'active' });
        onSuccess();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Create failed';
        setSubmitError(msg);
      }
    },
    [subgraph, createPrimitive, apolloClient, onSuccess],
  );

  const handleCapture = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      await processImage(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [processImage],
  );

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Group
        justify="space-between"
        px="sm"
        py="xs"
        style={{ borderBottom: '1px solid var(--mantine-color-default-border)', flexShrink: 0 }}
      >
        <Title order={5}>Create {entityLabel}</Title>
        <Group gap={4}>
          <Tooltip label={ocrAvailable ? 'Scan document to pre-fill' : 'Scan (upload photo)'} withArrow>
            <ActionIcon
              variant="subtle"
              onClick={handleCapture}
              size="sm"
              loading={scanning}
              aria-label={`Scan to create ${entityLabel}`}
            >
              <IconScan size={14} />
            </ActionIcon>
          </Tooltip>
          <ActionIcon
            variant="subtle"
            onClick={onClose}
            size="sm"
            aria-label={`Close create ${entityLabel} panel`}
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>
      </Group>

      {/* Hidden file input for camera/photo capture */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Scan status bar */}
      {statusMessage && (
        <Box px="sm" py={4} style={{ borderBottom: '1px solid var(--mantine-color-default-border)', flexShrink: 0 }}>
          <Group gap="xs">
            {status === 'done' ? (
              <IconCheck size={12} color="green" />
            ) : scanning ? (
              <Loader size={12} />
            ) : null}
            <Text size="xs" c={status === 'done' ? 'green' : 'dimmed'}>
              {statusMessage}
            </Text>
          </Group>
        </Box>
      )}

      {/* Backend-driven form (DRR-055: zero hardcoded fields) */}
      <ScrollArea style={{ flex: 1 }}>
        <Box p="sm">
          {submitError && (
            <Alert
              icon={<IconAlertTriangle size={16} />}
              color="red"
              mb="sm"
              onClose={() => setSubmitError(null)}
              withCloseButton
            >
              <Text size="sm">{submitError}</Text>
            </Alert>
          )}
          <EntityFormGenerator
            subgraph={subgraph}
            entityType={entityType}
            context={context}
            initialValues={initialValues}
            loading={mutationLoading}
            onSubmit={handleSubmit}
          />
        </Box>
      </ScrollArea>
    </Box>
  );
}
